import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Mail,
  MoreHorizontal,
  PencilLine,
  Phone,
  Power,
  Save,
  UserCog,
  X,
} from "lucide-react";
import { toast } from "react-hot-toast";
import {
  ActionMenu,
  ActionMenuItem,
  Badge,
  Button,
  Input,
} from "@/components/ui";
import { formatMoney } from "@/lib/money";
import { formatDateTime } from "@/lib/datetime";
import { cn } from "@/lib/utils";
import { ROLE_ICON_CONFIG } from "./roleIconConfig";
import type { AdminUserDetail, AdminUserDetailLabels } from "./types";

interface AdminUserHeaderProps {
  user: AdminUserDetail;
  labels: AdminUserDetailLabels;
  relevantCount: number;
  canEditDisplayName: boolean;
  isUpdatingDisplayName: boolean;
  onUpdateDisplayName: (name: string) => Promise<void>;
  canImpersonate: boolean;
  isImpersonating: boolean;
  onImpersonate: () => void;
  canToggleActive: boolean;
  isTogglingActive: boolean;
  onToggleActive: () => void;
}

interface HeaderFact {
  key: string;
  label: string;
  value: string;
  tone?: "default" | "positive" | "negative" | "muted";
}

/** Identity hero for admin user detail: avatar, name, status, contacts, key facts and actions. */
export function AdminUserHeader({
  user,
  labels,
  relevantCount,
  canEditDisplayName,
  isUpdatingDisplayName,
  onUpdateDisplayName,
  canImpersonate,
  isImpersonating,
  onImpersonate,
  canToggleActive,
  isTogglingActive,
  onToggleActive,
}: AdminUserHeaderProps) {
  const { t } = useTranslation("adminUsers");
  const [isEditingName, setIsEditingName] = useState(false);
  const [nameDraft, setNameDraft] = useState(user.name);

  useEffect(() => {
    setIsEditingName(false);
    setNameDraft(user.name);
  }, [user.userId, user.name]);

  const roleConfig = ROLE_ICON_CONFIG[user.role];
  const RoleIcon = roleConfig.icon;
  const showImpersonate = canImpersonate && user.isActive;

  const cancelEditingName = () => {
    setNameDraft(user.name);
    setIsEditingName(false);
  };

  const saveDisplayName = async () => {
    const nextName = nameDraft.trim();
    if (!nextName) {
      toast.error(labels.displayNameEmpty);
      return;
    }
    if (nextName === user.name) {
      setIsEditingName(false);
      return;
    }
    try {
      await onUpdateDisplayName(nextName);
      setIsEditingName(false);
    } catch {
      // Parent shows error toast; keep editor open for retry.
    }
  };

  const facts: HeaderFact[] = [
    {
      key: "wallet",
      label: labels.statWallet,
      value: formatMoney(user.walletBalance),
      tone:
        user.walletBalance > 0
          ? "positive"
          : user.walletBalance < 0
            ? "negative"
            : "default",
    },
    {
      key: "revenue",
      label: labels.statRevenue,
      value: formatMoney(user.totalRevenue),
    },
    {
      key: "relevant",
      label: labels.statRelevant,
      value: String(relevantCount),
    },
    {
      key: "lastLogin",
      label: t("detail.lastLogin"),
      value: user.lastLoginAt
        ? formatDateTime(user.lastLoginAt)
        : t("detail.lastLoginNever"),
      tone: user.lastLoginAt ? "default" : "muted",
    },
  ];

  return (
    <div className="border-surface-200 bg-surface-0 shadow-card animate-slide-up relative isolate overflow-hidden rounded-xl border">
      <div
        className="pointer-events-none absolute inset-x-0 top-0 -z-10 h-24 [mask-image:linear-gradient(to_bottom,black_30%,transparent)]"
        aria-hidden
      >
        <div className="from-primary-100 via-primary-50 to-accent-50 absolute inset-0 bg-gradient-to-r opacity-80" />
        <div className="bg-accent-300/30 absolute -top-16 -right-10 h-40 w-40 rounded-full blur-3xl" />
      </div>

      <div className="flex flex-col gap-4 px-5 pt-6 pb-5 md:flex-row md:items-start md:justify-between">
        <div className="flex min-w-0 items-start gap-4">
          <div
            className={cn(
              "shadow-elevated ring-surface-0 flex h-12 w-12 shrink-0 items-center justify-center rounded-xl ring-4 sm:h-16 sm:w-16",
              roleConfig.className,
            )}
            aria-hidden
          >
            <RoleIcon className="h-6 w-6 sm:h-7 sm:w-7" />
          </div>
          <div className="min-w-0 pt-0.5">
            <div className="flex min-w-0 flex-wrap items-center gap-2">
              {canEditDisplayName && isEditingName ? (
                <div className="flex min-w-0 flex-1 items-center gap-1">
                  <Input
                    value={nameDraft}
                    onChange={(event) => setNameDraft(event.target.value)}
                    onKeyDown={(event) => {
                      if (event.key === "Enter") {
                        event.preventDefault();
                        void saveDisplayName();
                      }
                      if (event.key === "Escape") {
                        cancelEditingName();
                      }
                    }}
                    placeholder={labels.displayNamePlaceholder}
                    disabled={isUpdatingDisplayName}
                    className="max-w-md font-semibold"
                    autoFocus
                    aria-label={labels.editDisplayName}
                  />
                  <button
                    type="button"
                    onClick={() => void saveDisplayName()}
                    disabled={isUpdatingDisplayName}
                    className="text-primary-600 hover:bg-primary-50 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg transition-colors disabled:opacity-50"
                    title={labels.editDisplayName}
                    aria-label={labels.editDisplayName}
                  >
                    <Save className="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    onClick={cancelEditingName}
                    disabled={isUpdatingDisplayName}
                    className="text-surface-500 hover:bg-surface-100 hover:text-surface-900 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg transition-colors disabled:opacity-50"
                    aria-label={t("detail.cancelEditLegalInfo")}
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ) : (
                <>
                  <h1 className="text-surface-900 min-w-0 truncate text-2xl font-semibold tracking-tight">
                    {user.name}
                  </h1>
                  {canEditDisplayName ? (
                    <button
                      type="button"
                      onClick={() => {
                        setNameDraft(user.name);
                        setIsEditingName(true);
                      }}
                      title={labels.editDisplayName}
                      aria-label={labels.editDisplayName}
                      className="text-surface-400 hover:bg-surface-100 hover:text-surface-700 inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md transition-colors"
                    >
                      <PencilLine className="h-4 w-4" />
                    </button>
                  ) : null}
                </>
              )}
              <Badge
                variant={user.isActive ? "success" : "default"}
                size="md"
                dot
              >
                {user.isActive ? labels.statusActive : labels.statusInactive}
              </Badge>
            </div>

            <div className="mt-2 flex flex-wrap items-center gap-1.5">
              <span className="border-surface-200 bg-surface-0 text-surface-700 inline-flex h-6 items-center gap-1.5 rounded-md border pr-2 pl-1 text-xs font-medium">
                <span
                  className={cn(
                    "flex h-4 w-4 items-center justify-center rounded",
                    roleConfig.className,
                  )}
                >
                  <RoleIcon
                    className="h-3 w-3"
                    aria-hidden
                  />
                </span>
                {labels.roleLabel}
              </span>
              {user.accountEmail ? (
                <a
                  href={`mailto:${user.accountEmail}`}
                  className="border-surface-200 bg-surface-0 text-surface-600 hover:border-surface-300 hover:text-surface-900 inline-flex h-6 max-w-full min-w-0 items-center gap-1.5 rounded-md border px-2 text-xs transition-colors"
                >
                  <Mail
                    className="text-surface-400 h-3.5 w-3.5 shrink-0"
                    aria-hidden
                  />
                  <span className="truncate">{user.accountEmail}</span>
                </a>
              ) : null}
              {user.contactPhone ? (
                <a
                  href={`tel:${user.contactPhone}`}
                  className="border-surface-200 bg-surface-0 text-surface-600 hover:border-surface-300 hover:text-surface-900 inline-flex h-6 items-center gap-1.5 rounded-md border px-2 text-xs tabular-nums transition-colors"
                >
                  <Phone
                    className="text-surface-400 h-3.5 w-3.5 shrink-0"
                    aria-hidden
                  />
                  {user.contactPhone}
                </a>
              ) : null}
            </div>
          </div>
        </div>

        {showImpersonate || canToggleActive ? (
          <div className="flex shrink-0 items-center gap-2">
            {showImpersonate ? (
              <Button
                variant="outline"
                leftIcon={<UserCog className="h-4 w-4 text-violet-600" />}
                onClick={onImpersonate}
                isLoading={isImpersonating}
              >
                {labels.impersonate}
              </Button>
            ) : null}
            {canToggleActive ? (
              <ActionMenu
                renderTrigger={(triggerProps) => (
                  <Button
                    {...triggerProps}
                    variant="outline"
                    size="icon"
                    isLoading={isTogglingActive}
                    aria-label={t("detail.moreActions")}
                    title={t("detail.moreActions")}
                  >
                    {isTogglingActive ? null : (
                      <MoreHorizontal
                        className="h-4 w-4"
                        aria-hidden
                      />
                    )}
                  </Button>
                )}
              >
                <ActionMenuItem
                  icon={Power}
                  iconClassName={
                    user.isActive ? "text-error-600" : "text-success-600"
                  }
                  className={user.isActive ? "text-error-700" : undefined}
                  onSelect={onToggleActive}
                >
                  {user.isActive ? labels.deactivate : labels.activate}
                </ActionMenuItem>
              </ActionMenu>
            ) : null}
          </div>
        ) : null}
      </div>

      <dl className="border-surface-200 bg-surface-50/60 dark:bg-surface-100/30 grid grid-cols-2 border-t lg:grid-cols-4">
        {facts.map((fact, index) => (
          <div
            key={fact.key}
            className={cn(
              "border-surface-200 min-w-0 px-5 py-3",
              index % 2 === 1 && "border-l",
              index >= 2 && "max-lg:border-t",
              index >= 1 && "lg:border-l",
            )}
          >
            <dt className="text-surface-500 text-xs font-medium">
              {fact.label}
            </dt>
            <dd
              className={cn(
                "mt-0.5 truncate font-semibold tracking-tight tabular-nums",
                fact.key === "lastLogin" ? "mt-1 text-sm" : "text-lg",
                fact.tone === "positive" && "text-success-700",
                fact.tone === "negative" && "text-error-700",
                fact.tone === "muted" && "text-surface-400 font-medium",
                (!fact.tone || fact.tone === "default") && "text-surface-900",
              )}
            >
              {fact.value}
            </dd>
          </div>
        ))}
      </dl>
    </div>
  );
}
